import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import { ALL_QUESTIONS, pickQuestions } from './questions';
import { GLOBAL_STYLES } from './styles';
import Menu from './components/Menu';
import GameScreen from './components/GameScreen';
import RoundEnd from './components/RoundEnd';
import GameEnd from './components/GameEnd';
import Leaderboard from './components/Leaderboard';

export default function App() {
  const [screen, setScreen] = useState('menu'); // menu | game | roundEnd | gameEnd | leaderboard
  const [difficulty, setDifficulty] = useState(null);
  const [round, setRound] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [chosenIdx, setChosenIdx] = useState(null);
  const [locked, setLocked] = useState(false);
  const [usedQuestions, setUsedQuestions] = useState(new Set());
  const [qualifies, setQualifies] = useState(false);

  const timerRef = useRef(null);
  const feedbackRef = useRef(null);

  const advanceQuestion = useCallback(() => {
    clearInterval(timerRef.current);
    setChosenIdx(null);
    setLocked(false);
    if (qIndex + 1 >= 5) {
      setScreen('roundEnd');
    } else {
      setQIndex((q) => q + 1);
      setTimeLeft(15);
    }
  }, [qIndex]);

  const handleAnswer = useCallback((choice, idx) => {
    if (locked) return;
    clearInterval(timerRef.current);
    setLocked(true);
    setChosenIdx(idx);

    const q = questions[qIndex];
    const correct = choice === q.a;

    if (correct) {
      const bonus = Math.ceil(timeLeft * 10);
      const base = { easy: 100, medium: 200, hard: 350, impossible: 500 }[difficulty];
      setScore((s) => s + base + bonus);
    }

    feedbackRef.current = setTimeout(advanceQuestion, 1200);
  }, [locked, questions, qIndex, timeLeft, difficulty, advanceQuestion]);

  // Timer tick
  useEffect(() => {
    if (screen !== 'game' || locked) return;
    clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleAnswer('__timeout__', -1);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [screen, qIndex, locked]); // eslint-disable-line

  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearTimeout(feedbackRef.current);
  }, []);

  function startGame(diff) {
    clearInterval(timerRef.current);
    clearTimeout(feedbackRef.current);
    setDifficulty(diff);
    setScore(0);
    setRound(1);
    const newUsed = new Set();
    const qs = pickQuestions(diff, newUsed);
    qs.forEach((q) => newUsed.add(q.q));
    setUsedQuestions(newUsed);
    setQuestions(qs);
    setQIndex(0);
    setTimeLeft(15);
    setChosenIdx(null);
    setLocked(false);
    setScreen('game');
  }

  async function nextRound() {
    if (round >= 3) {
      // Check if score qualifies for leaderboard
      const { data } = await supabase
        .from('leaderboard')
        .select('score')
        .order('score', { ascending: false })
        .limit(10);

      const scores = (data || []).map((e) => e.score);
      const qualif = scores.length < 10 || score > scores[scores.length - 1];
      setQualifies(qualif);
      setScreen('gameEnd');
    } else {
      const newRound = round + 1;
      setRound(newRound);
      const newUsed = new Set(usedQuestions);
      const qs = pickQuestions(difficulty, newUsed);
      qs.forEach((q) => newUsed.add(q.q));
      setUsedQuestions(newUsed);
      setQuestions(qs);
      setQIndex(0);
      setTimeLeft(15);
      setChosenIdx(null);
      setLocked(false);
      setScreen('game');
    }
  }

  function toMenu() {
    clearInterval(timerRef.current);
    clearTimeout(feedbackRef.current);
    setScreen('menu');
  }

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div className="app">
        <div className="bg-hex" />

        {screen === 'menu' && (
          <Menu
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            onPlay={startGame}
            onLeaderboard={() => setScreen('leaderboard')}
          />
        )}

        {screen === 'game' && (
          <GameScreen
            round={round}
            score={score}
            timeLeft={timeLeft}
            qIndex={qIndex}
            question={questions[qIndex]}
            locked={locked}
            chosenIdx={chosenIdx}
            difficulty={difficulty}
            onAnswer={handleAnswer}
            onExit={toMenu}
          />
        )}

        {screen === 'roundEnd' && (
          <RoundEnd round={round} score={score} onNext={nextRound} onExit={toMenu} />
        )}

        {screen === 'gameEnd' && (
          <GameEnd
            score={score}
            difficulty={difficulty}
            qualifies={qualifies}
            onLeaderboard={() => setScreen('leaderboard')}
            onPlay={() => startGame(difficulty)}
            onMenu={toMenu}
          />
        )}

        {screen === 'leaderboard' && (
          <Leaderboard onPlay={toMenu} />
        )}
      </div>
    </>
  );
}
